import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowRightUpIcon } from 'common-assets/assets/icons/arrow-right-up.svg';
import { ReactComponent as DocumentFilledIcon } from 'common-assets/assets/icons/document-filled.svg';
import { Icon } from '../../Icon/Icon';
import { ExternalLinkWcagAttributes } from '../../wcag/wcagAttributes';
import { LinkAnchorProps } from '../Link.types';
import { ScreenReaderOnly } from '../../ScreenReaderOnly/ScreenReaderOnly';

import useStyles from '../Link.style';

export const LinkAnchor: React.FC<LinkAnchorProps> = ({
  href,
  onClick,
  className,
  ariaLabel,
  testId,
  autoTestId,
  children,
  variant = 'underline',
  showExternalIcon = false,
  showDocumentIcon = false,
  onKeyDown,
  tabIndex,
  disabled,
  size = 'default',
  target,
  disableWcagAttributes,
}) => {
  const [t] = useTranslation();

  const c = useStyles({ variant, disabled, showExternalIcon, showDocumentIcon, size });

  const isExternalLink = target === '_blank';
  const showNewWindowHint = isExternalLink && !disableWcagAttributes;
  const newWindowText = showNewWindowHint ? t("externalLink.srOnlyText") : undefined;

  const getLinkAttributes = () => {
    if (disableWcagAttributes) {
      return {};
    }

    if (isExternalLink) {
      return ExternalLinkWcagAttributes;
    }

    if (target) {
      return { target, rel: 'noopener noreferrer' };
    }

    return {};
  };

  return (
    <a
      href={href}
      onClick={disabled ? undefined : onClick}
      className={clsx(c.root, className)}
      aria-label={ariaLabel && newWindowText ? `${ariaLabel} ${newWindowText}` : ariaLabel}
      aria-disabled={disabled}
      data-testid={testId}
      data-autotestid={autoTestId}
      onKeyDown={disabled ? undefined : onKeyDown}
      tabIndex={disabled ? -1 : tabIndex}
      {...getLinkAttributes()}
    >
      {(showDocumentIcon || showExternalIcon) ? (
        <>
          {showDocumentIcon && (
            <Icon className={c.documentIcon}>
              <DocumentFilledIcon />
            </Icon>
          )}
          <span className={c.linkText}>
            {children}
            {showExternalIcon && (
              <Icon className={c.icon}>
                <ArrowRightUpIcon />
              </Icon>
            )}
          </span>
        </>
      ) : (
        children
      )}
      {!ariaLabel && newWindowText && <ScreenReaderOnly text={` ${newWindowText}`} />}
    </a>
  );
};
